/* ============================================================
   ATELIER - PDP freight estimator
   Quotes real carrier freight for THIS product x qty WITHOUT
   touching the shopper's cart: a throwaway Storefront API cart
   (cartCreate -> deliveryGroups @defer withCarrierRates) runs
   through the same rating engine checkout uses. Verified to
   match the cart estimator to the cent (2026-07-16 spike:
   Orta 1500 bath to Melbourne 3000 = $161.36 on both paths).
   Shares the saved "deliver to" locality with the cart drawer
   and cart page via localStorage key 'atelierDeliverTo'.
   ============================================================ */
(function () {
  'use strict';
  var root = document.querySelector('[data-apg-freight]');
  if (!root) return;

  var SHOP = root.getAttribute('data-shop');
  var TOKEN = root.getAttribute('data-token');
  var API = root.getAttribute('data-api') || '2026-01';
  var SECTION = root.getAttribute('data-section');
  var LS_KEY = 'atelierDeliverTo';

  function q(sel) { return root.querySelector(sel); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function debounce(fn, ms) {
    var t; return function () { var a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(null, a); }, ms); };
  }

  /* ---------- shared "deliver to" (same key as cart drawer / cart page) ---------- */
  function loadLoc() {
    try {
      var v = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (v && v.s && v.pc && v.st) return v;
    } catch (e) { /* storage unavailable */ }
    return null;
  }
  function saveLoc(s, pc, st) {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ s: s, pc: pc, st: st })); } catch (e) { /* ignore */ }
  }

  /* ---------- current variant + qty (read FRESH each quote - survives the
     finish-swap AJAX contract, which updates .product-variant-id in place) ---------- */
  function currentVariant() {
    var el = document.querySelector('.product-variant-id');
    var v = (el && el.value) ? el.value : root.getAttribute('data-variant');
    return v ? String(v).trim() : null;
  }
  function currentQty() {
    var el = document.querySelector('.buy-row-qty input[name="quantity"], input.quantity__input[name="quantity"]');
    var n = el ? parseInt(el.value, 10) : 1;
    return (isFinite(n) && n > 0) ? Math.min(n, 999) : 1;
  }

  /* ---------- AU localities (same lazy <script> pattern as the cart) ---------- */
  var LOCS = null, locsLoading = null;
  function loadLocs() {
    if (LOCS) return Promise.resolve(LOCS);
    if (locsLoading) return locsLoading;
    locsLoading = new Promise(function (resolve) {
      if (window.atelierLocs) { LOCS = window.atelierLocs; resolve(LOCS); return; }
      var s = document.createElement('script');
      s.src = window.atelierLocalitiesUrl;
      s.onload = function () { LOCS = window.atelierLocs || []; resolve(LOCS); };
      s.onerror = function () { LOCS = []; console.warn('[atelier-freight] localities failed to load'); resolve(LOCS); };
      document.head.appendChild(s);
    });
    return locsLoading;
  }
  function searchLocs(s) {
    s = (s || '').trim().toLowerCase();
    if (s.length < 2 || !LOCS) return [];
    var byPc = /^\d+$/.test(s), res = [];
    for (var i = 0; i < LOCS.length && res.length < 8; i++) {
      var p = LOCS[i].split('|');
      var hay = byPc ? p[1] : p[0].toLowerCase();
      if (hay.indexOf(s) === 0) res.push(p);
    }
    return res;
  }
  function renderList(results) {
    var list = q('[data-fr-list]');
    if (!list) return;
    if (!results.length) { list.classList.remove('on'); list.innerHTML = ''; return; }
    list.innerHTML = results.map(function (p) {
      return '<li class="apg-fr-opt" role="option" data-s="' + esc(p[0]) + '" data-pc="' + p[1] + '" data-st="' + p[2] + '"><b>' + esc(p[0]) + '</b><span>' + p[2] + ' ' + p[1] + '</span></li>';
    }).join('');
    list.classList.add('on');
  }

  /* ---------- Storefront API (public token; parses plain JSON and
     multipart @defer responses with one splitter) ---------- */
  function gql(query, variables, signal) {
    return fetch('https://' + SHOP + '/api/' + API + '/graphql.json', {
      method: 'POST',
      signal: signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'multipart/mixed; deferSpec=20220824, application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (r) { return r.text(); }).then(function (text) {
      var parts = [];
      text.split('--graphql').forEach(function (p) {
        var i = p.indexOf('{');
        if (i === -1) return;
        try { parts.push(JSON.parse(p.slice(i))); } catch (e) { /* boundary noise */ }
      });
      return parts;
    });
  }
  var CREATE_Q =
    'mutation($vid:ID!,$qty:Int!,$addr:MailingAddressInput!){' +
    'cartCreate(input:{lines:[{merchandiseId:$vid,quantity:$qty}],' +
    'buyerIdentity:{countryCode:AU,deliveryAddressPreferences:[{deliveryAddress:$addr}]}}){' +
    'cart{id cost{subtotalAmount{amount}}}userErrors{message}}}';
  var RATES_Q =
    'query($id:ID!){cart(id:$id){id ...@defer{deliveryGroups(first:5,withCarrierRates:true){' +
    'nodes{deliveryOptions{title deliveryMethodType estimatedCost{amount}}}}}}}';

  function fetchQuote(variantId, qty, loc) {
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var timer = ctl && setTimeout(function () { ctl.abort(); }, 25000);
    var signal = ctl ? ctl.signal : undefined;
    var sub = null;
    return gql(CREATE_Q, {
      vid: 'gid://shopify/ProductVariant/' + variantId,
      qty: qty,
      addr: { zip: loc.pc, city: loc.s, province: loc.st, country: 'Australia' }
    }, signal).then(function (parts) {
      var d = parts[0] || {};
      var cc = d.data && d.data.cartCreate;
      if (!cc || !cc.cart) {
        var m = (cc && cc.userErrors && cc.userErrors[0] && cc.userErrors[0].message) ||
          (d.errors && d.errors[0] && d.errors[0].message) || 'cart create failed';
        throw new Error(m);
      }
      var cst = cc.cart.cost && cc.cart.cost.subtotalAmount;
      sub = cst ? parseFloat(cst.amount) : null;   /* this line's total, for the promo check */
      return gql(RATES_Q, { id: cc.cart.id }, signal);
    }).then(function (parts) {
      if (timer) clearTimeout(timer);
      var opts = [];
      parts.forEach(function (p) {
        var dg = p.data && p.data.cart && p.data.cart.deliveryGroups;
        (p.incremental || []).forEach(function (inc) {
          if (inc.data && inc.data.deliveryGroups) dg = inc.data.deliveryGroups;
        });
        ((dg && dg.nodes) || []).forEach(function (n) {
          (n.deliveryOptions || []).forEach(function (o) {
            if (!o.deliveryMethodType || o.deliveryMethodType === 'SHIPPING') opts.push(o);
          });
        });
      });
      if (!opts.length) return null;
      opts.sort(function (a, b) { return parseFloat(a.estimatedCost.amount) - parseFloat(b.estimatedCost.amount); });
      return { name: opts[0].title, price: parseFloat(opts[0].estimatedCost.amount), sub: sub };
    }).catch(function (e) {
      if (timer) clearTimeout(timer);
      throw e;
    });
  }

  /* ---------- quote cache: 12h in localStorage - carrier rates don't move
     hour to hour and the "estimate" wording covers drift; repeat visits and
     back-and-forth browsing must not re-fire ~5s carrier calcs ---------- */
  var CACHE_TTL = 43200000;
  /* atfrq2: entries written before the promo fix carry no subtotal, so the
     key was bumped rather than letting a stale hit suppress free shipping. */
  function cacheKey(v, n, loc) { return 'atfrq2:' + v + ':' + n + ':' + loc.pc + ':' + loc.s; }
  function getCache(k) {
    try {
      var v = JSON.parse(localStorage.getItem(k) || 'null');
      if (v && (Date.now() - v.t) < CACHE_TTL) return v;
    } catch (e) { /* ignore */ }
    return null;
  }
  function setCache(k, r) {
    try {
      localStorage.setItem(k, JSON.stringify({ t: Date.now(), n: r ? r.name : null, p: r ? r.price : null, s: r ? r.sub : null, none: !r }));
    } catch (e) { /* ignore */ }
  }
  function sweepCache() {
    try {
      var dead = [], i, key;
      for (i = 0; i < localStorage.length; i++) {
        key = localStorage.key(i);
        if (key && (key.indexOf('atfrq2:') === 0 || key.indexOf('atfrq:') === 0)) {
          var v = JSON.parse(localStorage.getItem(key) || 'null');
          if (!v || (Date.now() - v.t) >= CACHE_TTL) dead.push(key);
        }
      }
      dead.forEach(function (kk) { localStorage.removeItem(kk); });
    } catch (e) { /* ignore */ }
  }

  /* ---------- variant freight classes (smart re-quote) ----------
     Freight depends on weight (+price, for threshold rules) - not finish.
     Same product + same weight + same price => the shown quote is reusable
     INSTANTLY, no carrier call. Small products carry a variant map baked
     into the page ('M:' classes); huge ones (no map, e.g. 2000-variant
     drafts) resolve one variant at a time via a ~200ms node query ('F:'
     classes include the product handle so aliasing never crosses products). */
  var VMAP = null;
  (function () {
    var el = q('[data-fr-vmap]');
    if (el) { try { VMAP = JSON.parse(el.textContent); } catch (e) { VMAP = null; } }
  })();
  var CLASS_Q = 'query($id:ID!){node(id:$id){...on ProductVariant{weight price{amount} product{handle}}}}';
  function getClass(vid) {
    if (VMAP && VMAP[vid]) return Promise.resolve('M:' + VMAP[vid][0] + '|' + VMAP[vid][1]);
    var sk = 'atfrvc:' + vid, hit = null;
    try { hit = sessionStorage.getItem(sk); } catch (e) { /* ignore */ }
    if (hit) return Promise.resolve(hit === 'x' ? null : hit);
    return gql(CLASS_Q, { id: 'gid://shopify/ProductVariant/' + vid }).then(function (parts) {
      var nd = parts[0] && parts[0].data && parts[0].data.node;
      var cls = (nd && nd.product) ? 'F:' + nd.product.handle + '|' + nd.weight + '|' + nd.price.amount : null;
      try { sessionStorage.setItem(sk, cls || 'x'); } catch (e) { /* ignore */ }
      return cls;
    }).catch(function () { return null; });
  }

  /* ---------- render ---------- */
  var TRUCK = '<svg class="apg-fr-tk" viewBox="0 0 24 24"><path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7.5" cy="17.5" r="1.6"/><circle cx="17.5" cy="17.5" r="1.6"/></svg>';
  var EDIT = '<svg class="apg-fr-edit" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  function fmtRate(p) { return '$' + (p % 1 === 0 ? p.toFixed(0) : p.toFixed(2)); }
  function showMsg(t, err) { var m = q('[data-fr-msg]'); if (m) { m.textContent = t || ''; m.classList.toggle('err', !!err); } }
  function setLoading(on) { root.classList.toggle('loading', !!on); }
  function setDone(on) { root.classList.toggle('done', !!on); }
  function updateScope(n) {
    var sc = q('[data-fr-scope]');
    if (sc) { sc.textContent = n > 1 ? 'This item \u00D7' + n : 'This item only'; sc.classList.toggle('multi', n > 1); }
  }
  function fieldText(loc) {
    var inp = q('[data-fr-q]');
    if (inp) inp.value = loc ? loc.s + ', ' + loc.st + ' ' + loc.pc : '';
  }
  /* FREE SHIPPING promo. window.BSD_FREESHIP is emitted by
     snippets/atelier-freeship-rule ONLY while the promo gate is open (the same
     toggle + date window as the banners), so when the promo ends this returns
     the carrier rate untouched and nothing else changes.

     Judged on THIS item's line total, which matches the widget's "this item
     only" scope - never on the whole cart, so it cannot promise free shipping
     the customer will not get. If the price is unknown we show the rate, which
     is the safe direction to be wrong in. */
  function fsMaybeFree(r, loc, n) {
    var FS = window.BSD_FREESHIP;
    if (!FS || !r || !loc || r.price === 0) return r;
    /* the cart's own subtotal for this line - always available. The vmap is
       only a fallback: the snippet omits it for single-variant products, which
       is most of the catalogue, and that is what made this miss at first. */
    var cents = (typeof r.sub === 'number' && isFinite(r.sub)) ? Math.round(r.sub * 100) : null;
    if (cents === null) {
      var v = currentVariant();
      cents = (v && VMAP && VMAP[v]) ? VMAP[v][1] * (n || 1) : null;
    }
    if (typeof cents !== 'number') return r;
    return FS.ok(loc.pc, loc.st, cents) ? { name: r.name, price: 0, sub: r.sub } : r;
  }
  function renderResult(r, loc, n) {
    root.classList.remove('stale');
    showMsg('');
    updateScope(n);
    if (!r) { setDone(false); showMsg('No freight rates available to ' + loc.s + ' ' + loc.pc + '.'); return; }
    r = fsMaybeFree(r, loc, n);
    var free = r.price === 0;
    var res = q('[data-fr-res]');
    if (res) {
      res.innerHTML = TRUCK +
        '<span class="apg-fr-rtxt">' + esc(r.name) + ' to <b>' + esc(loc.s) + ' ' + loc.pc + '</b></span>' +
        '<span class="apg-fr-rprice' + (free ? ' free' : '') + '">' + (free ? 'FREE' : fmtRate(r.price)) + '</span>' +
        '<span class="apg-fr-spin" aria-hidden="true"></span>' + EDIT;
    }
    setDone(true);
    var note = q('[data-fr-note]');
    if (note) note.hidden = false;
  }

  /* ---------- quote orchestration ---------- */
  var quoting = false;    /* cache key of the in-flight quote, or false */
  var engaged = false;    /* a quote has been produced/attempted once */
  var lastGood = null;    /* {cls, lockey, qty, r} of the shown result - alias source */
  function locKey(loc) { return loc.pc + ':' + loc.s; }
  function noteGood(v, n, loc, r) {
    var entry = { cls: null, lockey: locKey(loc), qty: n, r: r };
    lastGood = entry;
    getClass(v).then(function (c) { if (lastGood === entry) entry.cls = c; });
  }
  function quote(loc) {
    var v = currentVariant(), n = currentQty();
    if (!v || !loc) return;
    engaged = true;
    updateScope(n);
    var k = cacheKey(v, n, loc);
    var cached = getCache(k);
    if (cached) {
      renderResult(cached.none ? null : { name: cached.n, price: cached.p, sub: cached.s }, loc, n);
      if (!cached.none) noteGood(v, n, loc, { name: cached.n, price: cached.p, sub: cached.s });
      return;
    }
    if (quoting === k) return;
    quoting = k;
    /* Same destination + qty, and the new variant is freight-identical to the
       one on screen? Reuse the quote instantly - no carrier call. */
    var alias = (lastGood && lastGood.cls && lastGood.lockey === locKey(loc) && lastGood.qty === n)
      ? getClass(v) : Promise.resolve(null);
    alias.then(function (cls) {
      if (quoting !== k) return; /* superseded by a newer quote */
      if (cls && lastGood && cls === lastGood.cls && lastGood.lockey === locKey(loc) && lastGood.qty === n) {
        setCache(k, lastGood.r);
        quoting = false;
        renderResult(lastGood.r, loc, n);
        noteGood(v, n, loc, lastGood.r);
        return;
      }
      /* Full quote. If a result is on screen, keep it visible (dimmed +
         spinner) instead of blanking back to the field for ~5s. */
      if (root.classList.contains('done')) {
        root.classList.add('stale');
      } else {
        setDone(false);
        setLoading(true);
        showMsg('Getting a live rate from the carrier...');
      }
      fetchQuote(v, n, loc).then(function (r) {
        setCache(k, r);
        if (currentVariant() !== v || currentQty() !== n) return; /* stale - a rerun is coming */
        renderResult(r, loc, n);
        if (r) noteGood(v, n, loc, r); else lastGood = null;
      }).catch(function (e) {
        if (currentVariant() !== v || currentQty() !== n) return;
        root.classList.remove('stale');
        setDone(false);
        showMsg('Could not fetch a freight estimate. Please try again.', true);
        console.error('[atelier-freight]', e);
      }).finally(function () {
        if (quoting === k) quoting = false;
        setLoading(false);
        root.classList.remove('stale');
      });
    });
  }
  var requote = debounce(function () {
    var loc = loadLoc();
    updateScope(currentQty());
    if (engaged && loc) quote(loc);
  }, 350);

  function pick(s, pc, st) {
    var list = q('[data-fr-list]');
    if (list) { list.classList.remove('on'); list.innerHTML = ''; }
    saveLoc(s, pc, st);
    fieldText({ s: s, pc: pc, st: st });
    quote({ s: s, pc: pc, st: st });
  }

  /* ---------- events ---------- */
  var debouncedSearch = debounce(function (v) { loadLocs().then(function () { renderList(searchLocs(v)); }); }, 120);
  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-fr-q]')) debouncedSearch(e.target.value);
  });
  root.addEventListener('focusin', function (e) {
    if (e.target.matches('[data-fr-q]')) loadLocs();
  });
  root.addEventListener('focusout', function (e) {
    if (e.target.matches('[data-fr-q]')) setTimeout(function () { var l = q('[data-fr-list]'); if (l) l.classList.remove('on'); }, 150);
  });
  root.addEventListener('click', function (e) {
    var opt = e.target.closest('.apg-fr-opt');
    if (opt) { pick(opt.getAttribute('data-s'), opt.getAttribute('data-pc'), opt.getAttribute('data-st')); return; }
    var res = e.target.closest('[data-fr-res]');
    if (res) {
      setDone(false);
      var inp = q('[data-fr-q]');
      if (inp) { inp.value = ''; inp.focus(); }
    }
  });

  /* qty changes (Dawn quantity-input dispatches change on the input) */
  document.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('input[name="quantity"]')) requote();
  });

  /* variant / finish-swap changes: the price node is re-rendered by both the
     Dawn variant flow and the finish-swap AJAX contract - observe it. */
  var priceEl = document.getElementById('price-' + SECTION);
  if (priceEl && 'MutationObserver' in window) {
    new MutationObserver(function () { requote(); }).observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  /* ---------- init: prefill from the shared key and quote IMMEDIATELY -
     the ~5s carrier wait happens while the shopper is still up at the
     gallery/title, so the answer is already there when they look ---------- */
  updateScope(currentQty());
  sweepCache();
  var saved = loadLoc();
  if (saved) {
    fieldText(saved);
    quote(saved);
  }
})();
